import { Currency, isCurrency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { LoyaltyInfo } from "@redotech/redo-loyalty-common/storefront-popup";
import { STORE_URL_HEADER } from "@redotech/shopify-sdk/rpc/custom-http-headers";
import { getLoyaltyCustomerInfo } from "@redotech/shopify-sdk/rpc/schema/get-loyalty-customer-info";
import { getPotentialPointsForPurchase } from "@redotech/shopify-sdk/rpc/schema/get-potential-points-for-purchase";
import { isDef } from "@redotech/util/checks";
import { useMemo } from "react";
import { RedoShopifyRpcClientContext } from "../../../client/redo-shopify-rpc-client";
import { ExtensionShopifyClientContext } from "../../../shopify";

export function useLoyaltyInfo(
  storeUrl: string,
): LoadState<LoyaltyInfo | undefined> {
  const shopifyRpcClient = useRequiredContext(RedoShopifyRpcClientContext);
  const extensionShopifyClient = useRequiredContext(
    ExtensionShopifyClientContext,
  );
  const loggedIn = useMemo(() => unsafeIsLoggedInToStorefront(), []);

  const itemsLoad = useLoad<
    { productId: string; price: number }[]
  >(async () => {
    const cart = await extensionShopifyClient.cartGet();

    return cart.items.map((item) => ({
      productId: item.product_id.toString(),
      // item.line_price is in cents, need to send in dollars
      price: (item.line_price ?? 0) / 100,
    }));
  }, []);

  const potentialPointsLoad = useLoad<number | undefined>(async () => {
    if (!isDef(itemsLoad.value)) {
      return undefined;
    }

    const potentialPointsResult = await shopifyRpcClient(
      getPotentialPointsForPurchase,
      {
        items: itemsLoad.value,
        customerId: loggedIn.loggedIn ? loggedIn.customerId : undefined,
        storeUrl_forCacheRoutingOnly: storeUrl,
      },
      { method: "GET", requestHeaders: { [STORE_URL_HEADER]: storeUrl } },
    );

    return potentialPointsResult.pointsToEarn;
  }, [loggedIn, itemsLoad.value]);

  const customerLoyaltyLoad = useLoad(async () => {
    if (!loggedIn.loggedIn) {
      return undefined;
    }

    const cart = await extensionShopifyClient.cartGet();
    const customerCurrency = isCurrency(cart.currency)
      ? cart.currency
      : Currency.USD;

    const loyaltyCustomerInfo = await shopifyRpcClient(
      getLoyaltyCustomerInfo,
      {
        customerId: loggedIn.customerId,
        currency: customerCurrency,
        storeUrl_forCacheRoutingOnly: storeUrl,
      },
      { method: "GET", requestHeaders: { [STORE_URL_HEADER]: storeUrl } },
    );

    return loyaltyCustomerInfo;
  }, [loggedIn, storeUrl]);

  const loyaltyInfoLoad = useLoad<LoyaltyInfo | undefined>(async () => {
    if (!isDef(potentialPointsLoad.value)) {
      return undefined;
    }

    if (!loggedIn.loggedIn) {
      return { loggedIn: false, pointsForPurchase: potentialPointsLoad.value };
    }

    if (!isDef(customerLoyaltyLoad.value)) {
      return undefined;
    }

    return {
      loggedIn: true,
      customerId: loggedIn.customerId,
      customerName: customerLoyaltyLoad.value.customerName,
      pointsBalance: customerLoyaltyLoad.value.pointsBalance,
      singlePointValue: customerLoyaltyLoad.value.singlePointValue,
      pointsForPurchase: potentialPointsLoad.value,
      loyaltyDashboardUrl: customerLoyaltyLoad.value.loyaltyDashboardUrl,
    };
  }, [loggedIn, potentialPointsLoad.value, customerLoyaltyLoad.value]);

  return loyaltyInfoLoad;
}

function unsafeIsLoggedInToStorefront():
  | { loggedIn: false }
  | { loggedIn: true; customerId: string } {
  const customerId: string | undefined = (
    globalThis as any
  ).ShopifyAnalytics?.meta.page.customerId?.toString();
  if (!customerId) {
    return { loggedIn: false };
  }

  return { loggedIn: true, customerId };
}
